import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { waitForBackendWake } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const u = localStorage.getItem("ceap_user");
      return u ? JSON.parse(u) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem("ceap_token"));
  const [loading, setLoading] = useState(false);

  // Ao montar o app, faz ping silencioso para acordar o backend (preview hibernation).
  useEffect(() => {
    waitForBackendWake(2, 1500).catch(() => {});
  }, []);

  const persist = (t, u) => {
    if (t) localStorage.setItem("ceap_token", t);
    if (u) localStorage.setItem("ceap_user", JSON.stringify(u));
    setToken(t);
    setUser(u);
  };

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      // Garante que o backend esteja acordado ANTES de tentar logar
      await waitForBackendWake(3, 2000).catch(() => {});
      const { data } = await api.post("/auth/login", { email, password });
      persist(data.access_token, data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (payload) => {
    setLoading(true);
    try {
      await waitForBackendWake(3, 2000).catch(() => {});
      const { data } = await api.post("/auth/register", payload);
      persist(data.access_token, data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("ceap_token");
    localStorage.removeItem("ceap_user");
    setToken(null);
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      localStorage.setItem("ceap_user", JSON.stringify(data));
      setUser(data);
      return data;
    } catch {
      logout();
    }
  }, [logout]);

  useEffect(() => {
    if (token && !user) {
      refresh();
    }
  }, [token, user, refresh]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
